import React from 'react';
import { useProperties } from '../../../hooks/useProperties';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

interface TimelineStatsProps {
  dateRange: [Date, Date];
  selectedAgents: string[];
  selectedTypes: string[];
}

export const TimelineStats: React.FC<TimelineStatsProps> = ({
  dateRange,
  selectedAgents,
  selectedTypes,
}) => {
  const { properties } = useProperties();

  // Filtrer les propriétés selon les critères
  const filteredProperties = properties.filter(property => {
    const inDateRange = property.createdAt >= dateRange[0] && property.createdAt <= dateRange[1];
    const matchesAgent = selectedAgents.length === 0 || selectedAgents.includes(property.agent);
    const matchesType = selectedTypes.length === 0 || selectedTypes.includes(property.type);
    return inDateRange && matchesAgent && matchesType;
  });

  // Calculer les délais moyens
  const soldProperties = filteredProperties.filter(p => p.status === 'sold');
  const rentedProperties = filteredProperties.filter(p => p.status === 'rented');

  const averageSaleTime = soldProperties.length > 0
    ? Math.round(soldProperties.reduce((acc, p) => 
        acc + differenceInDays(p.updatedAt, p.createdAt), 0) / soldProperties.length)
    : 0;

  const averageRentTime = rentedProperties.length > 0
    ? Math.round(rentedProperties.reduce((acc, p) => 
        acc + differenceInDays(p.updatedAt, p.createdAt), 0) / rentedProperties.length)
    : 0;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-white">
        Délais de transaction
      </h2>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            Vente
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Délai moyen
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {averageSaleTime} jours
                </span>
              </div>
              <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-2 rounded-full bg-primary-500"
                  style={{ width: `${Math.min((averageSaleTime / 180) * 100, 100)}%` }}
                />
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
              <h4 className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
                Dernières ventes
              </h4>
              <div className="space-y-2">
                {soldProperties.slice(0, 3).map(property => (
                  <div key={property.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      {property.type}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {format(property.updatedAt, 'dd MMM yyyy', { locale: fr })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            Location
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Délai moyen
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {averageRentTime} jours
                </span>
              </div>
              <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-2 rounded-full bg-primary-500"
                  style={{ width: `${Math.min((averageRentTime / 90) * 100, 100)}%` }}
                />
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
              <h4 className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
                Dernières locations
              </h4>
              <div className="space-y-2">
                {rentedProperties.slice(0, 3).map(property => (
                  <div key={property.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      {property.type}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {format(property.updatedAt, 'dd MMM yyyy', { locale: fr })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};